import type {
  ConversationNode,
  EditableGraph,
  Graph,
  GraphDetails,
} from 'src/types/chatbot';
import type {TranslationFunction} from 'src/types/language-support';
import type {Validation} from 'src/utils/validation';
// @ts-expect-error - TS7016 - Could not find a declaration file for module 'draft-js/lib/RawDraftContentBlock'. '/Users/diwaker/SpacedOut/ui-server/node_modules/draft-js/lib/RawDraftContentBlock.js' implicitly has an 'any' type.
import type {RawDraftContentBlock} from 'draft-js/lib/RawDraftContentBlock';
import type {Validation as Validation2} from 'src/utils/validation-2';
import type {EntityType} from 'src/types/ats-entities';
import type {LATQueueEntry} from 'src/types/live-agent-transfer';

import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useDispatch, useSelector} from 'react-redux';
import intersectionBy from 'lodash/intersectionBy';

import {convertFromRaw} from 'draft-js';
import {useReleaseFlag} from 'src/hooks/product-flags';

import {Link as RerouterLink} from 'src/rerouter';

import {handleHideQuestionSuggestion} from './reducer';

import {hasError} from 'src/utils/validation-2';
import {textDraftToString} from 'src/utils/draft';
import {useSmsContext} from './sms-context';
import {useOWBFields} from './hooks';
import {useMutableFieldStatus} from './api-hooks';
import {showGenericConfirm} from 'src/action-creators/modal';
import {bufferList} from 'src/utils/intl';
import classify from 'src/utils/classify';
import {
  editableNodeTypes,
  getChildEdges,
  findParents,
  replaceCurlyBracesWithPlaceholder,
} from './utils';

import {useWritebackAllowedAttributesV2} from 'src/hooks/useWritebackAllowedAttributes';
import {updateFlowConfig} from 'src/components/conversation-builder/thunks';
import {
  getWhatsappDetails,
  selectQuestionSimilarity,
  selectPresetQuestions,
} from 'src/selectors/chatbot';

import {useApi} from 'src/hooks/useApi';

import {useChatbotStateContext} from './chatbot-state-context';

import {ContextMenu2, MenuOption} from 'src/components/lib/context-menu';
import {OptOutMessage} from './common';

import WarningIcon from 'src/images/icons/warning-icon.svg';
import {MouseTip, useContextTip} from 'src/components/lib/mouse-tip/mouse-tip';

import {Link} from '@spaced-out/ui-design-system/lib/components/Link';
import {ReadOnlyDraftEditor} from './draft-editor';

import css from './conversation-node.css';
import {type AttributeMetaData} from 'src/types/writeback';


const getWhatsappAdditionalBlocks = (t: TranslationFunction) => [
  {
    key: 'hrule1',
    text: '',
    type: 'horizontal-rule',
    depth: 0,
    inlineStyleRanges: [],
    entityRanges: [],
    data: {},
  },
  {
    key: 'formlbl1',
    text: t('If_sending_via_WhatsApp', 'If sending via WhatsApp'),
    type: 'unstyled',
    depth: 0,
    inlineStyleRanges: [
      {
        offset: 0,
        length: 23,
        style: 'BOLD',
      },
    ],
    entityRanges: [],
    data: {},
  },
];

export default function ConversationNodeView({
  node,
  graph,
  flows,
  flowsLoaded,
  style,
  isJumpable,
  isFirstMessage,
  onRender,
  onSize,
  disabled,
  edgeError,
  nodeError,
  nodeWarning,
  entityType,
  isJourneyPreview,
}: {
  node: ConversationNode;
  graph: EditableGraph;
  flows: GraphDetails[];
  flowsLoaded: boolean;
  style: {
    left: number;
    top: number;
  };
  isJumpable: unknown;
  isFirstMessage: boolean;
  onRender: (arg1: ConversationNode) => unknown;
  onSize: (arg1: ConversationNode, arg2: number) => unknown;
  disabled?: unknown;
  edgeError: Validation | null | undefined;
  nodeError: Validation | null | undefined;
  nodeWarning: Validation2 | null | undefined;
  entityType: EntityType;
  isJourneyPreview: boolean;
}) {
  // @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
  const ref = React.useRef();
  const isSms = useSmsContext();
  const {t, language} = useLabelTranslation();
  const WHATSAPP_ADDITIONAL_BLOCKS = React.useMemo(
    () => getWhatsappAdditionalBlocks(t),
    [language],
  );

  const {state, dispatch} = useChatbotStateContext();
  const whatsappDetails = useSelector(getWhatsappDetails);

  /*
    TODO:
     Keshav: This is a temporary fix for the preview text, It will only work for the replacement of {{field_name}}.
     It will not work for other replacements like <field_name>.
   */
  const getReplacedPlaceholderBlocks = (block: any) => ({
    ...block,
    text: replaceCurlyBracesWithPlaceholder(
      block.text || '',
      // @ts-expect-error - TS18049 - 'whatsappDetails' is possibly 'null' or 'undefined'. | TS2345 - Argument of type 'WhatsAppFields' is not assignable to parameter of type '{ [key: string]: string; }'.
      whatsappDetails.additional_config_json.whatsapp_fields,
    ),
  });

  const getMergedJson = React.useCallback(
    // @ts-expect-error - TS7006 - Parameter 'node' implicitly has an 'any' type.
    (node) => {
      const isWhatsAppEnabled =
        node.is_root && whatsappDetails?.whatsapp_config_enabled;

      const returnValue = {
        blocks: [
          ...(node?.text_draft_json?.blocks || []),
          ...(isWhatsAppEnabled &&
          Array.isArray(whatsappDetails?.whatsapp_text_draft_json?.blocks)
            ? [
                ...WHATSAPP_ADDITIONAL_BLOCKS,
                ...whatsappDetails.whatsapp_text_draft_json.blocks.map(
                  getReplacedPlaceholderBlocks,
                ),
              ]
            : []),
        ],
        entityMap: {
          ...(node?.text_draft_json?.entityMap || {}),
          ...(whatsappDetails?.whatsapp_text_draft_json?.entityMap || {}),
        },
      } as const;
      return returnValue;
    },
    [node, whatsappDetails],
  );

  const enableQuestionSuggestions =
    useReleaseFlag('chatbotQuestionSuggestions') &&
    graph.bot_type === 'task_oriented' &&
    graph.status === 'init' &&
    // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'number'.
    !state?.hideQuestionSuggestions.includes(String(graph.id));

  onRender(node);
  React.useLayoutEffect(() => {
    // @ts-expect-error - TS2339 - Property 'offsetHeight' does not exist on type '{}'.
    onSize(node, ref.current?.offsetHeight || 0);
  }, [isSms, node, isFirstMessage, whatsappDetails?.whatsapp_text_draft_json]);

  const conditionsErrors = edgeError?.conditions?.errors;

  const {error, warning} = defineErrors(
    conditionsErrors,
    nodeError,
    // @ts-expect-error - TS2345 - Argument of type 'import("/Users/diwaker/SpacedOut/ui-server/src/utils/validation-2").Validation | null | undefined' is not assignable to parameter of type 'import("/Users/diwaker/SpacedOut/ui-server/src/utils/validation").Validation | null | undefined'.
    nodeWarning,
    t,
  );

  const showError = error.length > 0 || warning.length > 0;

  const matchingQuestion = useQuestionSuggestion(node);

  // This is a somewhat-temporary change to prevent deleting required
  // job id disposition nodes for application bots. This is a very
  // coarse, but specific, set of rules here.
  const undeletable =
    node.name === 'Job Order ID' &&
    node.type === 'disposition' &&
    graph.use_case === 'application_inbound_web';

  let body;
  // @ts-expect-error - TS7006 - Parameter 'blocks' implicitly has an 'any' type.
  const validateBlocks = (blocks): Array<RawDraftContentBlock> => {
    if (!Array.isArray(blocks)) {
      return [];
    }
    return blocks.filter((block) => {
      return (
        block &&
        typeof block === 'object' &&
        typeof block.key === 'string' &&
        typeof block.text === 'string' &&
        typeof block.type === 'string' &&
        typeof block.depth === 'number' &&
        Array.isArray(block.inlineStyleRanges) &&
        Array.isArray(block.entityRanges) &&
        typeof block.data === 'object'
      );
    });
  };

  const textDraftJson = React.useMemo(() => {
    const isWhatsAppEnabled =
      node.is_root && whatsappDetails?.whatsapp_config_enabled;
    return {
      blocks: validateBlocks(
        isWhatsAppEnabled && whatsappDetails.enable_whatsapp
          ? getMergedJson(node)?.blocks
          : // @ts-expect-error - TS2339 - Property 'text_draft_json' does not exist on type 'ConversationNode'.
            node?.text_draft_json?.blocks,
      ),
      entityMap:
        (isWhatsAppEnabled && whatsappDetails.enable_whatsapp
          ? getMergedJson(node)?.entityMap
          : // @ts-expect-error - TS2339 - Property 'text_draft_json' does not exist on type 'ConversationNode'.
            node?.text_draft_json?.entityMap) || {},
    };
  }, [node, whatsappDetails]);

  switch (node.type) {
    case 'summary':
      body = <SummaryBody node={node} graph={graph} />;
      break;
    case 'message':
    case 'file_upload':
    case 'scheduler':
    case 'text_to_apply':
      body = (
        <ReadOnlyDraftEditor
          entityType={entityType}
          isFirstSmsMessage={isFirstMessage && isSms}
          rte_json={node.text_draft_json}
        />
      );
      break;
    case 'question':
      body =
        node.text_draft_json && Array.isArray(node.text_draft_json.blocks) ? (
          <ReadOnlyDraftEditor
            entityType={entityType}
            isFirstSmsMessage={isFirstMessage && isSms}
            rte_json={textDraftJson}
          />
        ) : (
          <DraftTextBody
            node={node}
            isSms={isSms}
            isFirstMessage={isFirstMessage}
          />
        );
      break;
    case 'notification':
      body = (
        <ReadOnlyDraftEditor
          entityType={entityType}
          isFirstSmsMessage={isFirstMessage && isSms}
          rte_json={node.notification_email_subject}
        />
      );
      break;
    case 'condition':
      body = <ConditionBody node={node} graph={graph} />;
      break;
    case 'disposition':
      body = <DispositionBody node={node} entityType={entityType} />;
      break;
    case 'job_match':
      body = <JobMatchBody node={node} />;
      break;
    case 'live_agent_transfer':
      body = <LATBody node={node} graph={graph} />;
      break;
    case 'link_to_flow':
      body = (
        <LinkToFlowBody
          node={node}
          graph={graph}
          flows={flows}
          loaded={flowsLoaded}
        />
      );
      break;
    case 'dynamic_question':
      body = <DynamicQuestionBody graph={graph} node={node} />;
      break;
    default:
      body = (
        <DraftTextBody
          node={node}
          isSms={isSms}
          isFirstMessage={isFirstMessage}
        />
      );
      break;
  }

  return (
    <div
      className={classify(
        css.node,
        node.editable ? css.nodeEnabled : css.nodeDisabled,
        isJumpable && css.jumpable,
        error.length > 0 && css.error,
        warning.length > 0 && css.warning,
        enableQuestionSuggestions && matchingQuestion && css.suggestion,
      )}
      // @ts-expect-error - TS2322 - Type 'RefObject<unknown>' is not assignable to type 'Ref<HTMLDivElement> | undefined'.
      ref={ref}
      style={style}
      // @ts-expect-error - TS2322 - Type '(() => void) | null' is not assignable to type 'MouseEventHandler<HTMLDivElement> | undefined'.
      onClick={
        isJumpable
          ? () =>
              dispatch({
                type: 'ADD_JUMP',
                payload: {
                  nodeId: node.id,
                },
              })
          : null
      }
      // NOTE (kyle): this is required to prevent the jump field
      // from prematurely blurring.
      // @ts-expect-error - TS2322 - Type '((event: any) => any) | null' is not assignable to type 'MouseEventHandler<HTMLDivElement> | undefined'.
      onMouseDown={isJumpable ? (event: any) => event.preventDefault() : null}
    >
      <div className={css.title}>
        <span className={css.name}>
          {node.type === 'link_to_flow'
            ? t('Link_to_External_Flow', 'Link to External Flow')
            : node.name}
        </span>
        {showError && (
          <MouseTip content={<AlertList errors={error} warnings={warning} />}>
            <div className={classify(css.warningAlert)}>
              <WarningIcon style={{fontSize: 24}} />
            </div>
          </MouseTip>
        )}
        {!isJumpable &&
          node.editable &&
          (graph.bot_type === 'state_machine' ||
            (graph.bot_type === 'task_oriented' && !isJourneyPreview)) && (
            <ContextMenu2
              className={css.edit}
              menuContent={
                <NodeActions
                  node={node}
                  disabled={disabled || undeletable}
                  hasMultipleChildren={getChildEdges(graph, node.id).length > 1}
                  dispatch={dispatch}
                />
              }
            />
          )}
      </div>
      {isJourneyPreview ? (
        <div className={css.body}>{body}</div>
      ) : (
        <WithQuestionSuggestion
          node={node}
          // @ts-expect-error - TS2322 - Type '{ children: Element; node: ConversationNode; dispatch: ConversationDispatch; enableQuestionSuggestions: boolean | null | undefined; flowId: number; }' is not assignable to type 'IntrinsicAttributes & { node: any; children: any; enableQuestionSuggestions: any; flowId: any; }'.
          dispatch={dispatch}
          enableQuestionSuggestions={enableQuestionSuggestions}
          flowId={graph.id}
        >
          <div className={css.body}>{body}</div>
        </WithQuestionSuggestion>
      )}
    </div>
  );
}

// @ts-expect-error - TS7006 - Parameter 'node' implicitly has an 'any' type.
const useQuestionSuggestion = (node) => {
  const questionSimilarity = useSelector(selectQuestionSimilarity) ?? {};
  const presetQuestions = useSelector(selectPresetQuestions) ?? [];

  const isCustomQuestion = node.type === 'question' && !node.preset_question_id;
  const questionString =
    isCustomQuestion &&
    node.text_draft_json &&
    textDraftToString(node.text_draft_json);
  const hasMatchingPresetQuestion =
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{}'.
    questionSimilarity[questionString]?.length > 0 &&
    intersectionBy(
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{}'.
      questionSimilarity[questionString],
      // @ts-expect-error - TS2769 - No overload matches this call.
      presetQuestions,
      // @ts-expect-error - TS2339 - Property 'nlu_entity_type' does not exist on type 'unknown'. | TS2339 - Property 'intent' does not exist on type 'unknown'.
      ({nlu_entity_type, intent}) => intent + nlu_entity_type,
    ).length > 0;
  const matchingQuestion =
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{}'.
    hasMatchingPresetQuestion && questionSimilarity[questionString][0];

  return matchingQuestion;
};

function WithQuestionSuggestion({
  // @ts-expect-error - TS7031 - Binding element 'node' implicitly has an 'any' type.
  node,
  // @ts-expect-error - TS7031 - Binding element 'children' implicitly has an 'any' type.
  children,
  // @ts-expect-error - TS7031 - Binding element 'enableQuestionSuggestions' implicitly has an 'any' type.
  enableQuestionSuggestions,
  // @ts-expect-error - TS7031 - Binding element 'flowId' implicitly has an 'any' type.
  flowId,
}) {
  const {t, language} = useLabelTranslation();
  const {state, dispatch} = useChatbotStateContext();

  const matchingQuestion = useQuestionSuggestion(node);

  const tipContent = React.useMemo(
    () => (
      <div>
        {`${t('Preset_question_available', 'Preset question available')}!`}
        <p>
          <Link
            onClick={() => {
              if (matchingQuestion) {
                dispatch({
                  type: 'CONVERT_TO_PRESET',
                  payload: {
                    nodeId: node.id,
                  },
                });
              }
              handleClose();
            }}
            color="clickable"
            underline={false}
          >{`${t(
            'Convert_to_preset_question',
            'Convert to preset question',
          )} →`}</Link>

          <br />
          <span className={css.ignoreSuggestions}>
            {`${t('or', 'or')},`}
            {'\u00A0'}
            <Link
              color="clickable"
              underline={false}
              onClick={() => {
                handleHideQuestionSuggestion(flowId, dispatch);
                handleClose();
              }}
            >
              {` ${t('ignore_all_suggestions', 'ignore all suggestions')}`}
            </Link>
          </span>
        </p>
      </div>
    ),
    [matchingQuestion, language],
  );
  const [componentRef, handleContextMenu, handleClose, isActive] =
    useContextTip({
      id: 'resuableIdForSuggestions',
      content: tipContent,
      fixedTo: 'left',
      arrowPosition: 'end',
    });

  const handleSuggestionContext = (evt: React.MouseEvent) => {
    if (state.editingNode || state.insertingNode) {
      // do nothing
      return;
    }
    handleContextMenu(evt);
  };

  React.useEffect(() => {
    if (isActive && (state.editingNode || state.insertingNode)) {
      handleClose(); //make sure any open tips close while node is being edited
    }
  }, [state.editingNode, state.insertingNode]);

  if (!matchingQuestion || !enableQuestionSuggestions) {
    return children;
  } else {
    return (
      <div
        // @ts-expect-error - TS2322 - Type 'ReactRef<HTMLElement>' is not assignable to type 'Ref<HTMLDivElement> | undefined'.
        ref={componentRef}
        onContextMenu={handleContextMenu}
        onClick={handleClose}
        onMouseEnter={handleSuggestionContext}
        className={css.questionWithSuggestion}
      >
        {children}
      </div>
    );
  }
}

export function FeedbackNode({
  graph,
  style,
  editable,
  setShowFeedbackEditor,
  feedbackEnabled,
  setFlow,
  defaultGraph,
  readOnly,
  edgeError,
  nodeError,
  nodeWarning,
}: {
  graph: EditableGraph;
  style: {
    left: number;
    top: number;
  };
  editable: boolean;
  setShowFeedbackEditor: (arg1: boolean) => void;
  feedbackEnabled: boolean;
  setFlow?: (arg1: Graph) => void;
  defaultGraph: Graph;
  readOnly: boolean;
  edgeError?: Validation;
  nodeError?: Validation;
  nodeWarning?: Validation2;
}) {
  const {t} = useLabelTranslation();
  const dispatch = useDispatch();

  const handleUpdateFeedbackEnabled = () => {
    dispatch(
      updateFlowConfig(graph.id, {
        ...graph.flow_config,
        feedback_enabled: !feedbackEnabled,
      }),
    ).then((updatedFlowConfig) => {
      if (setFlow) {
        //@ts-expect-error
        setFlow({...defaultGraph, flow_config: updatedFlowConfig});
      }
    });
  };

  const {canFlowUpdate} = useMutableFieldStatus(!editable, 'question');

  return (
    <>
      <div className={classify(css.node, css.nodeEnabled)} style={style}>
        <div className={css.title}>
          <span className={css.name}>{`${t('Feedback', 'Feedback')} ${
            feedbackEnabled ? '' : `(${t('Disabled', 'Disabled')})`
          }`}</span>
          <ContextMenu2
            className={css.edit}
            menuContent={
              <>
                <MenuOption onClick={() => setShowFeedbackEditor(true)}>
                  {canFlowUpdate
                    ? t('View_and_Edit', 'View and Edit')
                    : editable
                    ? t('Edit', 'Edit')
                    : t('View', 'View')}
                </MenuOption>
                {!readOnly && (
                  <MenuOption onClick={handleUpdateFeedbackEnabled}>
                    {feedbackEnabled
                      ? t('Disable', 'Disable')
                      : t('Enable', 'Enable')}
                  </MenuOption>
                )}
              </>
            }
          />
        </div>
        <div className={css.body}>
          <ReadOnlyDraftEditor
            entityType={graph.anchor_entity_type}
            isFirstSmsMessage={false}
            rte_json={defaultGraph.flow_config.feedback_question}
          />
        </div>
      </div>
    </>
  );
}

function defineErrors(
  conditionsErrors: undefined | Array<string>,
  nodeError: Validation | null | undefined,
  nodeWarning: Validation | null | undefined,
  t: any,
) {
  const error: Array<string> = [];
  const warning: Array<string> = [];

  if (conditionsErrors?.includes('EMPTY_CONDITION_SET')) {
    error.push(
      `${t(
        'There_are_no_conditions_that_lead_to_this_path_Add_a_condition_to_the_response_node_above_to_make_this_a_valid_path',
        'There are no conditions that lead to this path. Add a condition to the response node above to make this a valid path',
      )}.`,
    );
  } else if (conditionsErrors?.includes('IDENTICAL_CONDITION_SET')) {
    error.push(
      `${t(
        'This_path_has_shares_the_same_set_of_conditions_as_one_of_its_siblings_Add_or_remove_a_condition_to_make_it_valid',
        'This path has shares the same set of conditions as one of its siblings. Add or remove a condition to make it valid',
      )}.`,
    );
  }

  if (nodeError?.errors?.includes('INVALID_FIRST_MESSAGE')) {
    error.push(
      `${t(
        'To_avoid_carrier_filtering_The_first_message_in_a_conversation_must_be_a_question',
        'To avoid carrier filtering. The first message in a conversation must be a question',
      )}.`,
    );
  }

  // @ts-expect-error - TS2345 - Argument of type 'import("/Users/diwaker/SpacedOut/ui-server/src/utils/validation").Validation | null | undefined' is not assignable to parameter of type 'import("/Users/diwaker/SpacedOut/ui-server/src/utils/validation-2").Validation | null | undefined'.
  if (hasError(nodeWarning, 'answers', 'MISSING_T2A_KEYWORD')) {
    warning.push(
      `${t(
        'This_node_includes_Text_to_Apply_keywords_that_have_not_been_added_to_Text_to_Apply_settings',
        'This node includes Text to Apply keywords that have not been added to Text to Apply settings',
      )}.`,
    );
  }

  // @ts-expect-error - TS2345 - Argument of type 'import("/Users/diwaker/SpacedOut/ui-server/src/utils/validation").Validation | null | undefined' is not assignable to parameter of type 'import("/Users/diwaker/SpacedOut/ui-server/src/utils/validation-2").Validation | null | undefined'.
  if (hasError(nodeWarning, 'text_draft_json', 'DISCOVER_ATTR_WARNING')) {
    warning.push(
      `${t(
        'This_node_contains_job_match_variable_To_avoid_errors_when_using_a_job_match_variable_please_ensure_a_job_match_node_is_present_before_this_node',
        'This node contains job match variable. To avoid errors when using a job match variable, please ensure a job match node is present before this node',
      )}.`,
    );
  }

  // @ts-expect-error - TS2345 - Argument of type 'import("/Users/diwaker/SpacedOut/ui-server/src/utils/validation").Validation | null | undefined' is not assignable to parameter of type 'import("/Users/diwaker/SpacedOut/ui-server/src/utils/validation-2").Validation | null | undefined'.
  if (hasError(nodeWarning, 'text_draft_json', 'NO_QUESTION_MARK')) {
    warning.push(
      `${t(
        'This_node_does_not_appear_to_include_a_question',
        'This node does not appear to include a question',
      )}.`,
    );
  }

  // @ts-expect-error - TS2345 - Argument of type 'import("/Users/diwaker/SpacedOut/ui-server/src/utils/validation").Validation | null | undefined' is not assignable to parameter of type 'import("/Users/diwaker/SpacedOut/ui-server/src/utils/validation-2").Validation | null | undefined'.
  if (hasError(nodeWarning, 'text_draft_json', 'NO_LEAF_QUESTION')) {
    warning.push(
      `${t(
        'Last_node_cannot_be_a_question',
        'Last node cannot be a question',
      )}.`,
    );
  }

  // @ts-expect-error - TS2345 - Argument of type 'import("/Users/diwaker/SpacedOut/ui-server/src/utils/validation").Validation | null | undefined' is not assignable to parameter of type 'import("/Users/diwaker/SpacedOut/ui-server/src/utils/validation-2").Validation | null | undefined'.
  if (hasError(nodeWarning, 'text_draft_json', 'NO_LEAF_MEETING_NODE')) {
    warning.push(
      `${t(
        'Last_node_cannot_be_a_meeting_node',
        'Last node cannot be a meeting node',
      )}.`,
    );
  }

  if (
    hasError(
      // @ts-expect-error - TS2345 - Argument of type 'import("/Users/diwaker/SpacedOut/ui-server/src/utils/validation").Validation | null | undefined' is not assignable to parameter of type 'import("/Users/diwaker/SpacedOut/ui-server/src/utils/validation-2").Validation | null | undefined'.
      nodeWarning,
      'writeback_field_id',
      'MISSING_APPLICATION_DISPOSITION_VALUE',
    )
  ) {
    warning.push(
      `${t(
        'A_disposition_node_needs_a_valid_ATS_Writeback_Field',
        'A disposition node needs a valid ATS Writeback Field',
      )}`,
    );
  }

  // @ts-expect-error - TS2345 - Argument of type 'import("/Users/diwaker/SpacedOut/ui-server/src/utils/validation").Validation | null | undefined' is not assignable to parameter of type 'import("/Users/diwaker/SpacedOut/ui-server/src/utils/validation-2").Validation | null | undefined'.
  if (hasError(nodeWarning, 'text_draft_json', 'SMS_RECOMMEND_QUESTION')) {
    warning.push(
      `${t(
        'To_avoid_deliverability_issues_for_SMS_touchpoints_we_recommend_beginning_with_a_question_node',
        'To avoid deliverability issues for SMS touchpoints, we recommend beginning with a question node',
      )}.`,
    );
  }

  if (nodeError?.errors?.includes('MISSING_PRECEDING_QUESTION')) {
    warning.push(
      `${t(
        'Live_Chat_flows_require_a_question_before_live_agent_transfer_can_begin',
        'Live Chat flows require a question before live agent transfer can begin',
      )}`,
    );
  }

  return {
    error,
    warning,
  };
}

function AlertList(props: {errors?: string[]; warnings?: string[]}) {
  return (
    <>
      <ul>
        {props.errors?.map((error: string) => (
          <li>{error}</li>
        ))}
        {props.warnings?.map((warning: string) => (
          <li>{warning}</li>
        ))}
      </ul>
    </>
  );
}

// @ts-expect-error - TS7031 - Binding element 'node' implicitly has an 'any' type. | TS7031 - Binding element 'disabled' implicitly has an 'any' type. | TS7031 - Binding element 'hasMultipleChildren' implicitly has an 'any' type. | TS7031 - Binding element 'dispatch' implicitly has an 'any' type.
function NodeActions({node, disabled, hasMultipleChildren, dispatch}) {
  const {t} = useLabelTranslation();
  const reduxDispatch = useDispatch();
  const {canFlowUpdate} = useMutableFieldStatus(disabled, node.type);

  return (
    <>
      {editableNodeTypes.includes(node.type) && (
        <MenuOption
          onClick={() =>
            dispatch({
              type: 'EDIT_NODE',
              payload: node.id,
            })
          }
        >
          {canFlowUpdate
            ? t('View_and_Edit', 'View and Edit')
            : disabled
            ? t('View', 'View')
            : t('Edit', 'Edit')}
        </MenuOption>
      )}
      {!disabled && (
        <MenuOption
          onClick={async () => {
            const yes =
              !hasMultipleChildren ||
              (await reduxDispatch(
                showGenericConfirm({
                  title: t('Node_Removal', 'Node Removal'),
                  text: `${t(
                    'Are_you_sure_you_want_to_delete_all_the_branches_below_this_node_If_not_remove_the_branches_you_wish_to_eliminate_and_try_removing_the_node_again',
                    'Are you sure you want to delete all the branches below this node? If not, remove the branches you wish to eliminate and try removing the node again',
                  )}.`,
                  confirmText: t('Remove', 'Remove'),
                }),
              ));
            if (yes) {
              dispatch({
                type: 'REMOVE_NODE',
                payload: node.id,
              });
            }
          }}
        >
          {t('Remove', 'Remove')}
        </MenuOption>
      )}
    </>
  );
}

// @ts-expect-error - TS7031 - Binding element 'node' implicitly has an 'any' type. | TS7031 - Binding element 'isSms' implicitly has an 'any' type. | TS7031 - Binding element 'isFirstMessage' implicitly has an 'any' type.
function DraftTextBody({node, isSms, isFirstMessage}) {
  const {t, language} = useLabelTranslation();
  const body = React.useMemo(() => {
    // fallback for link to flow node because it doesn't have .text_draft_json
    if (node.type === 'link_to_flow') {
      return t('Link_to_external_flow', 'Link to external flow');
    }

    const contentJson =
      node.type === 'notification'
        ? node.notification_email_subject
        : node.text_draft_json != null
        ? node.text_draft_json
        : null;
    const text = contentJson ? convertFromRaw(contentJson).getPlainText() : '';

    return text;
  }, [node, language]);

  return (
    <div className={css.draftContent}>
      <div>{body}</div>
      {isFirstMessage && isSms && <OptOutMessage />}
    </div>
  );
}

// @ts-expect-error - TS7031 - Binding element 'node' implicitly has an 'any' type.
function JobMatchBody({node}) {
  const body = React.useMemo(() => {
    const contentJson = node.job_search_criteria_data.matching_messages_rte;
    const text = contentJson ? convertFromRaw(contentJson).getPlainText() : '';

    return text;
  }, [node]);

  return <div className={css.draftContent}>{body}</div>;
}

// @ts-expect-error - TS7031 - Binding element 'node' implicitly has an 'any' type. | TS7031 - Binding element 'entityType' implicitly has an 'any' type.
function DispositionBody({node, entityType}) {
  const {t} = useLabelTranslation();
  const writebackAttributes = useWritebackAllowedAttributesV2(entityType);

  const {getWbFieldId} = useOWBFields();

  const fieldName = React.useMemo(
    () =>
      (node.writeback_field_id &&
        writebackAttributes?.attribute_meta_data?.find(
          (attribute: AttributeMetaData) =>
            getWbFieldId(attribute) === node.writeback_field_id,
        )?.attribute_name) ||
      String(node.writeback_field_id),
    [writebackAttributes, node],
  );

  return (
    <div style={{flexFlow: 'column'}}>
      <span>
        {`${t('Set_field', 'Set field')} `}

        <b>{fieldName}</b>
        {` ${t('to', 'to')} `}
      </span>
      {node.disposition_writeback_value ? (
        <span>{String(node.disposition_writeback_value)}</span>
      ) : node.text_draft_json != null ? (
        <ReadOnlyDraftEditor
          entityType={entityType}
          rte_json={node.text_draft_json}
          isFirstSmsMessage={false}
        />
      ) : null}
    </div>
  );
}

// @ts-expect-error - TS7031 - Binding element 'node' implicitly has an 'any' type. | TS7031 - Binding element 'graph' implicitly has an 'any' type.
function ConditionBody({node, graph}) {
  const {t} = useLabelTranslation();
  const limit = 2;
  const {
    condition_ruleset: {expressions},
  } = node;
  const operatorLabels = {
    true: {
      equals: t('is_exactly', 'is exactly'),
      in: t('is_one_of', 'is one of'),
      starts_with: t('starts_with', 'starts with'),
      ends_with: t('ends_with', 'ends with'),
      greater_than: t('is_greater_than', 'is greater than'),
      less_than: t('is_less_than', 'is less than'),
      contains: t('contains', 'contains'),
    },
    false: {
      equals: t('is_not', 'is not'),
      in: t('is_not_one_of', 'is not one of'),
      starts_with: t('does_not_start_with', 'does not start with'),
      ends_with: t('does_not_end_with', 'does not end with'),
      greater_than: t('is_less_than', 'is less than'),
      less_than: t('is_greater_than', 'is greater than'),
      contains: t('does_not_contain', 'does not contain'),
    },
  } as const;
  return (
    <div className={css.conditions}>
      {/* @ts-expect-error - TS7006 - Parameter 'expression' implicitly has an 'any' type. | TS7006 - Parameter 'index' implicitly has an 'any' type. */}
      {expressions.slice(0, limit).map((expression, index) => (
        <div key={expression.id}>
          <span className={css.if}>
            {index === 0 ? t('IF', 'IF') : t('AND', 'AND')}
          </span>{' '}
          <span className={css.left}>
            {expression.dynamic_field ? (
              `<${expression.dynamic_field}>`
            ) : (
              <>
                “
                {graph.nodes.get(expression.node_id_answer)?.name ||
                  expression.node_id_answer}
                ”
              </>
            )}
          </span>{' '}
          <span className={css.operator}>
            {expression.is_not
              ? // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly equals: string; readonly in: string; readonly starts_with: string; readonly ends_with: string; readonly greater_than: string; readonly less_than: string; readonly contains: string; }'.
                operatorLabels.false[expression.operator]
              : // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly equals: string; readonly in: string; readonly starts_with: string; readonly ends_with: string; readonly greater_than: string; readonly less_than: string; readonly contains: string; }'.
                operatorLabels.true[expression.operator]}
          </span>{' '}
          <span className={css.right}>
            {expression.condition_value instanceof Array ? (
              expression.condition_value.length > 0 ? (
                bufferList(
                  // @ts-expect-error - TS7006 - Parameter 'value' implicitly has an 'any' type.
                  expression.condition_value.map((value) => (
                    <span key={value}>'{value}'</span>
                  )),
                  <span>, </span>,
                  <span>{t('or', 'or')}</span>,
                )
              ) : (
                <span>{t('empty', 'empty')}</span>
              )
            ) : (
              <span>{`'${
                expression.condition_value || t('null', 'null')
              }'`}</span>
            )}
          </span>
        </div>
      ))}
      {expressions.length > limit && (
        <div>{`+ ${expressions.length - limit} ${t('more', 'more')}`}</div>
      )}
    </div>
  );
}

// @ts-expect-error - TS7031 - Binding element 'node' implicitly has an 'any' type. | TS7031 - Binding element 'graph' implicitly has an 'any' type.
function SummaryBody({node, graph}) {
  const {t} = useLabelTranslation();
  const parents = findParents(node, graph);

  const limit = 2;
  const {summary_items} = node;
  const question_nodes: ConversationNode[] = parents.filter(
    (node) => node.type === 'question',
  );
  return (
    <div className={css.conditions}>
      {question_nodes.slice(0, limit).map((node) => {
        const summary_item = summary_items.find(
          // @ts-expect-error - TS7006 - Parameter 'item' implicitly has an 'any' type.
          (item) => item.node_id === node.id,
        );
        return (
          <div key={node.id}>
            <span className={css.left}>
              “{summary_item?.short_name || node.name}”
            </span>
            <span className={css.operator}>:</span>{' '}
            <span className={css.right}>
              {summary_item?.enabled
                ? t('enabled', 'enabled')
                : t('disabled', 'disabled')}
            </span>
          </div>
        );
      })}
      {summary_items.length > limit && (
        <div>{`+ ${summary_items.length - limit} ${t('more', 'more')}`}</div>
      )}
    </div>
  );
}

// @ts-expect-error - TS7031 - Binding element 'node' implicitly has an 'any' type. | TS7031 - Binding element 'graph' implicitly has an 'any' type.
function LATBody({node, graph}) {
  const {t} = useLabelTranslation();
  const latQueueRequest = useApi<LATQueueEntry[]>('messages_v2/lat/queues');

  const queueId =
    node.additional_configs_json.queue_routing_rules?.default_queue_id;

  if (
    latQueueRequest.isLoading ||
    queueId == null ||
    !Array.isArray(latQueueRequest.result)
  ) {
    return null;
  }

  return (
    <div>
      {`${t('LAT_with_queue', 'LAT with queue')}: `}
      {latQueueRequest.result?.find(
        (queue: LATQueueEntry) => queue.id === queueId,
      )?.name ?? t('missing_name', 'missing name')}
    </div>
  );
}

// @ts-expect-error - TS7031 - Binding element 'node' implicitly has an 'any' type. | TS7031 - Binding element 'graph' implicitly has an 'any' type. | TS7031 - Binding element 'flows' implicitly has an 'any' type. | TS7031 - Binding element 'loaded' implicitly has an 'any' type.
function LinkToFlowBody({node, graph, flows, loaded}) {
  const {t} = useLabelTranslation();
  const targetFlowId = node.additional_configs_json.target_flow_id;
  // @ts-expect-error - TS7006 - Parameter 'flow' implicitly has an 'any' type.
  const targetFlow = flows.find((flow) => flow.id === targetFlowId);

  return (
    <div>
      {loaded && targetFlowId != null && (
        <RerouterLink
          to={`/conversations/${targetFlowId}`}
          target="blank"
          // @ts-expect-error - TS2322 - Type '{ children: Element; to: string; target: string; title: string; }' is not assignable to type 'IntrinsicAttributes & { to?: LocationType | ((arg1: RerouterLocation) => LocationType) | null | undefined; ... 6 more ...; handleExtension?: boolean | undefined; }'.
          title={t(
            'open_this_flow_in_new_window',
            'open this flow in new window',
          )}
        >
          <strong>{targetFlow?.name}</strong>
        </RerouterLink>
      )}
    </div>
  );
}

// @ts-expect-error - TS7031 - Binding element 'graph' implicitly has an 'any' type. | TS7031 - Binding element 'node' implicitly has an 'any' type.
function DynamicQuestionBody({graph, node}) {
  const {t} = useLabelTranslation();
  // These don't currently have any content
  const writebackAttributes = useWritebackAllowedAttributesV2(
    graph.anchor_entity_type,
  );
  const writebackField = writebackAttributes?.attribute_meta_data.find(
    (m: AttributeMetaData) => m.field_id === node.writeback_field_id,
  );
  // This q&a node will write back to <strong>{}</strong>

  return (
    <div>
      {writebackField ? (
        <span>
          {t(
            'Dynamic_questions_writing_back_to',
            'Dynamic questions writing back to',
          )}{' '}
          <strong>{writebackField.display_name}</strong>
        </span>
      ) : (
        `${t(
          'Writebacks_not_yet_configured_for_this_qa_node',
          'Writebacks not yet configured for this q&a node',
        )}`
      )}
    </div>
  );
}
